import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { WorkArea } from '@snaphire/contracts';
import { Dropdown } from './dropdown';
import { Empty, ErrorState, Loading } from './ui';

export type { WorkArea } from '@snaphire/contracts';
export const workAreaLabels: Record<WorkArea, string> = {
  HR: 'HR',
  ADMINISTRATIVE: 'Административный отдел',
};
const areaOrder: WorkArea[] = ['HR', 'ADMINISTRATIVE'];
type Owner = { id: string; workspaceId: string };
const cacheKey = (owner: Owner) => `snaphire.work-area.v1:${owner.workspaceId}:${owner.id}`;

export function permittedWorkAreas(value: unknown): WorkArea[] {
  return areaOrder.filter((area) => Array.isArray(value) && value.includes(area));
}
export function readWorkArea(owner: Owner): WorkArea | null {
  try {
    const value = localStorage.getItem(cacheKey(owner));
    return areaOrder.includes(value as WorkArea) ? (value as WorkArea) : null;
  } catch {
    return null;
  }
}
export function writeWorkArea(owner: Owner, area: WorkArea): void {
  try {
    localStorage.setItem(cacheKey(owner), area);
  } catch {
    // Выбор продолжает работать, если браузер запрещает локальное хранение.
  }
}
export function resolveWorkArea(allowed: WorkArea[], preferred: WorkArea | null): WorkArea | null {
  return preferred && allowed.includes(preferred) ? preferred : allowed[0] || null;
}
export function workAreaHome(area: WorkArea): '/' | '/administrative' {
  return area === 'HR' ? '/' : '/administrative';
}
export function isSharedWorkAreaPath(path: string): boolean {
  return (
    path.startsWith('/account/') ||
    ['workspace', 'accounts', 'permission-groups', 'service-accounts'].some(
      (resource) => path === `/admin/${resource}` || path.startsWith(`/admin/${resource}/`),
    )
  );
}
export function canOpenWorkAreaPath(path: string, allowed: WorkArea[], active: WorkArea | null) {
  if (!active || !allowed.includes(active)) return false;
  if (isSharedWorkAreaPath(path)) return true;
  return path === '/administrative' || path.startsWith('/administrative/')
    ? active === 'ADMINISTRATIVE'
    : active === 'HR' && allowed.includes('HR');
}
export function workAreaForPath(path: string): WorkArea | null {
  if (isSharedWorkAreaPath(path)) return null;
  return path === '/administrative' || path.startsWith('/administrative/')
    ? 'ADMINISTRATIVE'
    : 'HR';
}

/** Private data may belong to the previous area. Keep only the current identity/permissions. */
export async function clearWorkAreaQueries(client: QueryClient): Promise<void> {
  const privateQuery = (query: { queryKey: readonly unknown[] }) => query.queryKey[0] !== 'access';
  await client.cancelQueries({ predicate: privateQuery });
  client.removeQueries({ predicate: privateQuery });
}

export function useWorkArea(
  owner: Owner | null,
  grants: unknown,
  client: QueryClient,
  path?: string,
) {
  const ownerKey = owner ? cacheKey(owner) : '';
  const [selection, setSelection] = useState(() => ({
    ownerKey,
    area: owner ? readWorkArea(owner) : null,
  }));
  const [changing, setChanging] = useState(false);
  const [, synchronize] = useState(0);
  const allowed = permittedWorkAreas(grants);
  const preferred =
    selection.ownerKey === ownerKey ? selection.area : owner ? readWorkArea(owner) : null;
  const active = resolveWorkArea(allowed, preferred);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const current = useRef({ ownerKey, allowed, path });
  current.current = { ownerKey, allowed, path };
  const pendingRoute = useRef<{
    ownerKey: string;
    area: WorkArea;
    resolve: (accepted: boolean) => void;
  } | null>(null);
  const previous = useRef({ ownerKey, active });
  const signature = allowed.join(',');
  const needsCleanup =
    previous.current.ownerKey === ownerKey &&
    previous.current.active !== active &&
    previous.current.active !== null;
  useEffect(() => {
    const pending = pendingRoute.current;
    if (!pending) return;
    if (pending.ownerKey !== ownerKey || !allowed.includes(pending.area)) {
      pendingRoute.current = null;
      pending.resolve(false);
    } else if (path !== undefined && workAreaForPath(path) === pending.area) {
      pendingRoute.current = null;
      pending.resolve(true);
    }
  }, [ownerKey, signature, path]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pendingRoute.current?.resolve(false);
    };
  }, []);

  useEffect(() => {
    if (owner && active) writeWorkArea(owner, active);
    if (needsCleanup) {
      let current = true;
      void clearWorkAreaQueries(client).then(() => {
        if (!current) return;
        previous.current = { ownerKey, active };
        synchronize((value) => value + 1);
      });
      return () => {
        current = false;
      };
    }
    previous.current = { ownerKey, active };
  }, [ownerKey, owner?.id, owner?.workspaceId, active, signature, client]);

  const change = async (next: WorkArea, transition?: () => Promise<unknown>) => {
    if (!owner || !allowed.includes(next) || inFlight.current || next === active) return false;
    inFlight.current = true;
    setChanging(true);
    try {
      await clearWorkAreaQueries(client);
      const authorized = () =>
        mounted.current &&
        current.current.ownerKey === ownerKey &&
        current.current.allowed.includes(next);
      if (!authorized()) return false;
      if (transition) {
        await transition();
        if (!authorized()) return false;
        if (current.current.path !== undefined && workAreaForPath(current.current.path) !== next) {
          const accepted = await new Promise<boolean>((resolve) => {
            pendingRoute.current = { ownerKey, area: next, resolve };
          });
          if (!accepted || !authorized()) return false;
        }
      }
      writeWorkArea(owner, next);
      previous.current = { ownerKey, active: next };
      setSelection({ ownerKey, area: next });
      return true;
    } finally {
      pendingRoute.current = null;
      inFlight.current = false;
      setChanging(false);
    }
  };
  return { allowed, active, changing: changing || needsCleanup, change };
}

export function WorkAreaSwitcher({
  allowed,
  active,
  disabled,
  onChange,
}: {
  allowed: WorkArea[];
  active: WorkArea | null;
  disabled?: boolean;
  onChange: (area: WorkArea) => void;
}) {
  if (allowed.length < 2 || !active) return null;
  return (
    <div className="work-area-switcher">
      <Dropdown
        label="Переключить пространство"
        className="work-area-trigger"
        minPopupWidth={264}
        options={allowed.map((area) => ({ id: area, label: workAreaLabels[area] }))}
        selected={[active]}
        disabled={disabled}
        onChange={(values) => {
          const area = values[0] as WorkArea;
          if (allowed.includes(area)) onChange(area);
        }}
      />
    </div>
  );
}

export function WorkAreaContent({
  ready,
  changing,
  allowed,
  active,
  path,
  children,
  error,
  retry,
}: {
  ready: boolean;
  changing: boolean;
  allowed: WorkArea[];
  active: WorkArea | null;
  path: string;
  children: ReactNode;
  error?: Error | null;
  retry?: () => void;
}) {
  if (!ready && error) return <ErrorState error={error} retry={retry} />;
  if (!ready || changing) return <Loading />;
  if (!active)
    return (
      <Empty
        title="Доступ к пространствам не назначен"
        subtitle="Обратитесь к администратору, чтобы получить доступ."
      />
    );
  if (!canOpenWorkAreaPath(path, allowed, active)) return <Loading />;
  return children;
}
